import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  IconButton,
  LinearProgress,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useTranslation } from 'react-i18next';
import {
  copyEvohomeScheduleToZones,
  fetchEvohomeSchedule,
  putEvohomeSchedule,
  type EvohomeDevice,
  type EvohomeScheduleDay,
  type EvohomeScheduleSwitchpoint,
} from '../../services/evohomeApi';
import { getDemoRestrictionSnackbarProps } from '../../constants/demoRestrictions';
import { EVOHOME_CMD_COOLDOWN_MS } from './evohomeCmdGuard';
import { evohomeTempColor } from './evohomeTempColor';

const ACCENT = '#2391FF';
const HEAT = '#FF8A65';
const SURFACE = 'rgba(255,255,255,0.04)';
const SURFACE_BORDER = 'rgba(255,255,255,0.08)';
const PRESS_TRANSITION =
  'transform 140ms cubic-bezier(0.2, 0.9, 0.3, 1), background-color 160ms ease, border-color 160ms ease, box-shadow 180ms ease, color 160ms ease, opacity 90ms ease';

const DAYS = [
  'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday',
] as const;

type DayKey = (typeof DAYS)[number];

function emptyWeek(temp = 18): EvohomeScheduleDay[] {
  return DAYS.map((d) => ({
    day_of_week: d,
    switchpoints: [{ time_of_day: '00:00:00', heat_setpoint: temp }],
  }));
}

function cloneWeek(days: EvohomeScheduleDay[]): EvohomeScheduleDay[] {
  return days.map((d) => ({
    day_of_week: d.day_of_week,
    switchpoints: (d.switchpoints || []).map((sp) => ({ ...sp })),
  }));
}

function formatTimeDisplay(tod: string): string {
  const s = String(tod || '');
  return s.length >= 5 ? s.slice(0, 5) : s;
}

function parseTimeToMinutes(tod: string): number {
  const [h, m] = formatTimeDisplay(tod).split(':').map((x) => parseInt(x, 10));
  return (h || 0) * 60 + (m || 0);
}

function minutesToTod(mins: number): string {
  const m = ((mins % (24 * 60)) + 24 * 60) % (24 * 60);
  const hh = Math.floor(m / 60);
  const mm = m % 60;
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}:00`;
}

function parseTimeInput(raw: string): number | null {
  const s = String(raw || '').trim().replace('.', ':');
  const m = /^(\d{1,2}):(\d{1,2})$/.exec(s);
  if (!m) return null;
  const hh = parseInt(m[1], 10);
  const mm = parseInt(m[2], 10);
  if (hh < 0 || hh > 23 || mm < 0 || mm > 59) return null;
  const snapped = Math.round(mm / 10) * 10;
  const adjH = snapped === 60 ? (hh + 1) % 24 : hh;
  const adjM = snapped === 60 ? 0 : snapped;
  return adjH * 60 + adjM;
}

function parseTempInput(raw: string, minT: number, maxT: number, step: number): number | null {
  const n = Number(String(raw || '').replace(',', '.').replace('°', '').trim());
  if (!Number.isFinite(n)) return null;
  const snapped = Math.round(n / step) * step;
  if (snapped < minT - 0.01 || snapped > maxT + 0.01) return null;
  return Math.min(maxT, Math.max(minT, Math.round(snapped * 2) / 2));
}

/** +/- con hold-to-accelerate. */
function RoundControl({
  disabled,
  onStep,
  ariaLabel,
  children,
}: {
  disabled?: boolean;
  onStep: () => void;
  ariaLabel: string;
  children: React.ReactNode;
}) {
  const timerRef = useRef<number | null>(null);
  const delayRef = useRef(380);

  const clear = useCallback(() => {
    if (timerRef.current != null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    delayRef.current = 380;
  }, []);

  const scheduleNext = useCallback(() => {
    timerRef.current = window.setTimeout(() => {
      onStep();
      delayRef.current = Math.max(45, delayRef.current * 0.72);
      scheduleNext();
    }, delayRef.current);
  }, [onStep]);

  const startHold = useCallback((e: React.PointerEvent) => {
    if (disabled) return;
    e.preventDefault();
    try {
      (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
    } catch { /* ignore */ }
    onStep();
    delayRef.current = 380;
    scheduleNext();
  }, [disabled, onStep, scheduleNext]);

  useEffect(() => () => clear(), [clear]);

  return (
    <IconButton
      aria-label={ariaLabel}
      disabled={disabled}
      onPointerDown={startHold}
      onPointerUp={clear}
      onPointerCancel={clear}
      onPointerLeave={clear}
      onContextMenu={(e) => e.preventDefault()}
      sx={{
        width: 44,
        height: 44,
        color: '#fff',
        bgcolor: SURFACE,
        border: `1px solid ${SURFACE_BORDER}`,
        transition: PRESS_TRANSITION,
        WebkitTapHighlightColor: 'transparent',
        touchAction: 'none',
        userSelect: 'none',
        '&:hover:not(.Mui-disabled)': {
          bgcolor: 'rgba(255,255,255,0.08)',
          borderColor: ACCENT,
        },
        '&:active:not(.Mui-disabled)': { transform: 'scale(0.9)' },
        '&.Mui-disabled': {
          color: '#666',
          borderColor: '#2a2a2a',
          bgcolor: '#121212',
        },
      }}
    >
      {children}
    </IconButton>
  );
}

export interface EvohomeScheduleEditorProps {
  zone: EvohomeDevice;
  siblingZones: EvohomeDevice[];
  isDemoUser?: boolean;
  onDemoActionAttempt?: () => void;
  onClose: () => void;
  onSaved?: (info: {
    zoneId: number;
    days: EvohomeScheduleDay[];
    copiedToIds?: number[];
  }) => void;
  nestedBackRef?: React.MutableRefObject<(() => boolean) | null>;
}

const EvohomeScheduleEditor: React.FC<EvohomeScheduleEditorProps> = ({
  zone,
  siblingZones,
  isDemoUser,
  onDemoActionAttempt,
  onClose,
  onSaved,
  nestedBackRef,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snack, setSnack] = useState<string | null>(null);
  const [days, setDays] = useState<EvohomeScheduleDay[]>(emptyWeek());
  const [baseline, setBaseline] = useState<string>('');
  const [activeDay, setActiveDay] = useState<DayKey>('monday');
  const [caps, setCaps] = useState({
    min: 1,
    max: 6,
    minT: 5,
    maxT: 35,
    step: 0.5,
  });
  const [editIdx, setEditIdx] = useState<number | null>(null);
  const [editTimeMin, setEditTimeMin] = useState(0);
  const [editTemp, setEditTemp] = useState(20);
  const [copyDayOpen, setCopyDayOpen] = useState(false);
  const [copyDayTargets, setCopyDayTargets] = useState<Record<string, boolean>>({});
  const [copyZoneOpen, setCopyZoneOpen] = useState(false);
  const [copyZoneTargets, setCopyZoneTargets] = useState<Record<number, boolean>>({});
  const [cooldownUntil, setCooldownUntil] = useState(0);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [editingTime, setEditingTime] = useState(false);
  const [editingTemp, setEditingTemp] = useState(false);
  const [timeDraft, setTimeDraft] = useState('');
  const [tempDraft, setTempDraft] = useState('');
  const [copyProgress, setCopyProgress] = useState<{
    current: number;
    total: number;
    name: string;
  } | null>(null);

  const dirty = useMemo(
    () => JSON.stringify(days) !== baseline,
    [days, baseline],
  );

  const dayLabel = useCallback(
    (d: string) => t(`climateSystem.schedules.days.${d}`, { defaultValue: d }),
    [t],
  );

  const dayShort = useCallback(
    (d: string) => t(`climateSystem.schedules.daysShort.${d}`, { defaultValue: d.slice(0, 3) }),
    [t],
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchEvohomeSchedule(zone.id);
      const week = cloneWeek(
        data.daily_schedules?.length === 7 ? data.daily_schedules : emptyWeek(),
      );
      setDays(week);
      setBaseline(JSON.stringify(week));
      const c = data.capabilities || {};
      setCaps({
        min: Number(c.min_switchpoints_per_day ?? 1),
        max: Number(c.max_switchpoints_per_day ?? 6),
        minT: Number(c.min_heat_setpoint ?? zone.min_setpoint ?? 5),
        maxT: Number(c.max_heat_setpoint ?? zone.max_setpoint ?? 35),
        step: Number(c.setpoint_value_resolution ?? 0.5),
      });
    } catch (e: any) {
      const d = e?.response?.data?.detail;
      setError(typeof d === 'object' ? (d.message || JSON.stringify(d)) : (d || e?.message || 'Error'));
      setDays(emptyWeek());
      setBaseline('');
    } finally {
      setLoading(false);
    }
  }, [zone.id, zone.min_setpoint, zone.max_setpoint]);

  useEffect(() => {
    void load();
  }, [load]);

  const activeSps = useMemo(() => {
    const day = days.find((d) => d.day_of_week === activeDay);
    return day?.switchpoints || [];
  }, [days, activeDay]);

  const blockDemo = () => {
    if (!isDemoUser) return false;
    onDemoActionAttempt?.();
    return true;
  };

  const updateActiveSps = (next: EvohomeScheduleSwitchpoint[]) => {
    setDays((prev) =>
      prev.map((d) =>
        d.day_of_week === activeDay
          ? { ...d, switchpoints: [...next].sort((a, b) => a.time_of_day.localeCompare(b.time_of_day)) }
          : d,
      ),
    );
  };

  const openEdit = (idx: number) => {
    const sp = activeSps[idx];
    if (!sp) return;
    setEditIdx(idx);
    setEditTimeMin(parseTimeToMinutes(sp.time_of_day));
    setEditTemp(Number(sp.heat_setpoint));
    setEditingTime(false);
    setEditingTemp(false);
    setError(null);
  };

  const commitEdit = () => {
    if (editIdx == null) return;
    const next = activeSps.map((sp, i) =>
      i === editIdx
        ? { time_of_day: minutesToTod(editTimeMin), heat_setpoint: editTemp }
        : sp,
    );
    const times = next.map((s) => s.time_of_day);
    if (new Set(times).size !== times.length) {
      setError(t('climateSystem.schedules.errorDuplicateTime'));
      return;
    }
    updateActiveSps(next);
    setEditingTime(false);
    setEditingTemp(false);
    setEditIdx(null);
  };

  const deletePeriod = () => {
    if (editIdx == null) return;
    if (activeSps.length <= caps.min) {
      setError(t('climateSystem.schedules.errorMinPeriods', { min: caps.min }));
      return;
    }
    updateActiveSps(activeSps.filter((_, i) => i !== editIdx));
    setEditIdx(null);
  };

  const addPeriod = () => {
    if (activeSps.length >= caps.max) {
      setError(t('climateSystem.schedules.errorMaxPeriods', { max: caps.max }));
      return;
    }
    const used = new Set(activeSps.map((s) => s.time_of_day));
    let candidate = 6 * 60;
    for (let i = 0; i < 24 * 6; i += 1) {
      const tod = minutesToTod(candidate);
      if (!used.has(tod)) {
        const next = [
          ...activeSps,
          { time_of_day: tod, heat_setpoint: Math.min(caps.maxT, Math.max(caps.minT, 20)) },
        ];
        updateActiveSps(next);
        setEditIdx(next.findIndex((s) => s.time_of_day === tod));
        setEditTimeMin(candidate);
        setEditTemp(20);
        return;
      }
      candidate = (candidate + 10) % (24 * 60);
    }
  };

  const handleSave = async () => {
    if (blockDemo()) return;
    if (Date.now() < cooldownUntil) return;
    setSaving(true);
    setError(null);
    setCooldownUntil(Date.now() + EVOHOME_CMD_COOLDOWN_MS);
    try {
      const data = await putEvohomeSchedule(zone.id, days);
      const week = cloneWeek(data.daily_schedules || days);
      setDays(week);
      setBaseline(JSON.stringify(week));
      setSnack(t('climateSystem.schedules.saved', { name: zone.nombre }));
      onSaved?.({ zoneId: zone.id, days: week });
    } catch (e: any) {
      const d = e?.response?.data?.detail;
      setError(typeof d === 'object' ? (d.message || JSON.stringify(d)) : (d || e?.message || 'Error'));
    } finally {
      setSaving(false);
    }
  };

  const applyCopyDay = () => {
    const targets = DAYS.filter((d) => d !== activeDay && copyDayTargets[d]);
    if (!targets.length) {
      setCopyDayOpen(false);
      return;
    }
    const src = activeSps.map((s) => ({ ...s }));
    setDays((prev) =>
      prev.map((d) =>
        targets.includes(d.day_of_week as DayKey)
          ? { ...d, switchpoints: src.map((s) => ({ ...s })) }
          : d,
      ),
    );
    setCopyDayOpen(false);
    setSnack(t('climateSystem.schedules.copyDayDone'));
  };

  const otherZones = siblingZones.filter((z) => z.id !== zone.id);

  const handleCopyZone = async () => {
    if (blockDemo()) return;
    const targets = otherZones.filter((z) => copyZoneTargets[z.id]).map((z) => z.id);
    if (!targets.length) {
      setCopyZoneOpen(false);
      return;
    }
    setSaving(true);
    setError(null);
    setCooldownUntil(Date.now() + EVOHOME_CMD_COOLDOWN_MS);
    setCopyZoneOpen(false);
    try {
      let week = days;
      if (dirty) {
        setCopyProgress({ current: 0, total: targets.length, name: zone.nombre });
        const saved = await putEvohomeSchedule(zone.id, days);
        week = cloneWeek(saved.daily_schedules || days);
        setDays(week);
        setBaseline(JSON.stringify(week));
      }
      // Una sola lectura origen en servidor + N PUTs; progreso por lote
      setCopyProgress({
        current: 0,
        total: targets.length,
        name: otherZones.find((z) => z.id === targets[0])?.nombre || '',
      });
      // Copiar de a una para progreso visible y no saturar de golpe
      const copiedNames: string[] = [];
      for (let i = 0; i < targets.length; i += 1) {
        const tid = targets[i];
        const tName = otherZones.find((z) => z.id === tid)?.nombre || String(tid);
        setCopyProgress({ current: i + 1, total: targets.length, name: tName });
        const data = await copyEvohomeScheduleToZones(zone.id, [tid], week);
        const n = data?.copied?.[0]?.target_nombre || tName;
        copiedNames.push(n);
      }
      setSnack(t('climateSystem.schedules.copyZoneDone', {
        from: zone.nombre,
        to: copiedNames.join(', ') || String(targets.length),
      }));
      onSaved?.({
        zoneId: zone.id,
        days: week.map((d) => ({
          day_of_week: d.day_of_week,
          switchpoints: d.switchpoints.map((s) => ({ ...s })),
        })),
        copiedToIds: targets,
      });
    } catch (e: any) {
      const d = e?.response?.data?.detail;
      setError(typeof d === 'object' ? (d.message || JSON.stringify(d)) : (d || e?.message || 'Error'));
    } finally {
      setSaving(false);
      setCopyProgress(null);
    }
  };

  const requestClose = useCallback(() => {
    if (dirty) setDiscardOpen(true);
    else onClose();
  }, [dirty, onClose]);

  useEffect(() => {
    if (!nestedBackRef) return undefined;
    nestedBackRef.current = () => {
      requestClose();
      return true;
    };
    return () => {
      if (nestedBackRef.current) nestedBackRef.current = null;
    };
  }, [nestedBackRef, requestClose]);

  return (
    <Box sx={{ color: 'white', px: 0.5, pb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
        <IconButton
          size="small"
          onClick={requestClose}
          aria-label={t('common.back', { defaultValue: 'Volver' })}
          sx={{ color: ACCENT }}
        >
          <ArrowBackIcon sx={{ fontSize: 20 }} />
        </IconButton>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{
            fontSize: 12, fontWeight: 700, color: '#999',
            letterSpacing: '0.06em', textTransform: 'uppercase',
          }}>
            {t('climateSystem.schedules.editorTitle')}
          </Typography>
          <Typography sx={{
            fontSize: 16, fontWeight: 700, color: '#fff',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {zone.nombre}
            {dirty ? ' · *' : ''}
          </Typography>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress sx={{ color: ACCENT }} />
        </Box>
      ) : (
        <>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.65, mb: 1.5 }}>
            {DAYS.map((d) => {
              const selected = activeDay === d;
              return (
                <Box
                  key={d}
                  component="button"
                  type="button"
                  onClick={() => setActiveDay(d)}
                  sx={{
                    appearance: 'none',
                    border: selected ? `1px solid ${HEAT}` : `1px solid ${SURFACE_BORDER}`,
                    bgcolor: selected ? `${HEAT}22` : SURFACE,
                    color: selected ? '#fff' : 'rgba(255,255,255,0.72)',
                    borderRadius: '12px',
                    minHeight: 36,
                    minWidth: 44,
                    px: 1,
                    py: 0.5,
                    cursor: 'pointer',
                    fontWeight: selected ? 700 : 500,
                    fontSize: 12,
                    transition: PRESS_TRANSITION,
                    boxShadow: selected ? `0 6px 18px ${HEAT}28, inset 0 0 0 1px ${HEAT}33` : 'none',
                    WebkitTapHighlightColor: 'transparent',
                    '&:active': { transform: 'scale(0.94)' },
                  }}
                >
                  {dayShort(d)}
                </Box>
              );
            })}
          </Box>

          <Typography sx={{
            fontSize: 12, fontWeight: 700, color: '#999', mb: 0.75,
            letterSpacing: '0.06em', textTransform: 'uppercase',
          }}>
            {dayLabel(activeDay)}
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.65, mb: 1.5 }}>
            {activeSps.map((sp, idx) => (
              <Box
                key={`${sp.time_of_day}-${idx}`}
                component="button"
                type="button"
                onClick={() => openEdit(idx)}
                sx={{
                  appearance: 'none',
                  textAlign: 'left',
                  border: `1px solid ${SURFACE_BORDER}`,
                  bgcolor: SURFACE,
                  color: '#fff',
                  borderRadius: '12px',
                  px: 1.25,
                  py: 1.1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  transition: PRESS_TRANSITION,
                  WebkitTapHighlightColor: 'transparent',
                  '&:hover': {
                    borderColor: `${HEAT}88`,
                    bgcolor: 'rgba(255,255,255,0.07)',
                  },
                  '&:active': { transform: 'scale(0.98)' },
                }}
              >
                <Typography sx={{ fontSize: 16, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                  {formatTimeDisplay(sp.time_of_day)}
                </Typography>
                <Typography sx={{
                  fontSize: 16,
                  fontWeight: 700,
                  color: evohomeTempColor(sp.heat_setpoint),
                }}
                >
                  {Number(sp.heat_setpoint).toFixed(1)} °C
                </Typography>
              </Box>
            ))}
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 0.75, mb: 2 }}>
            <Button
              size="small"
              startIcon={<AddIcon />}
              disabled={activeSps.length >= caps.max || saving}
              onClick={addPeriod}
              sx={{
                color: ACCENT,
                border: `1px solid ${ACCENT}55`,
                textTransform: 'none',
                borderRadius: '10px',
                justifyContent: 'flex-start',
              }}
            >
              {t('climateSystem.schedules.addPeriod')}
            </Button>
            <Button
              size="small"
              startIcon={<ContentCopyIcon />}
              disabled={saving}
              onClick={() => {
                const init: Record<string, boolean> = {};
                DAYS.forEach((d) => { if (d !== activeDay) init[d] = false; });
                setCopyDayTargets(init);
                setCopyDayOpen(true);
              }}
              sx={{
                color: '#ccc',
                border: `1px solid ${SURFACE_BORDER}`,
                textTransform: 'none',
                borderRadius: '10px',
                justifyContent: 'flex-start',
              }}
            >
              {t('climateSystem.schedules.copyDay')}
            </Button>
            {otherZones.length > 0 && (
              <Button
                size="small"
                startIcon={<ContentCopyIcon />}
                disabled={saving}
                onClick={() => {
                  const init: Record<number, boolean> = {};
                  otherZones.forEach((z) => { init[z.id] = false; });
                  setCopyZoneTargets(init);
                  setCopyZoneOpen(true);
                }}
                sx={{
                  color: '#ccc',
                  border: `1px solid ${SURFACE_BORDER}`,
                  textTransform: 'none',
                  borderRadius: '10px',
                  justifyContent: 'flex-start',
                }}
              >
                {t('climateSystem.schedules.copyZone')}
              </Button>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            <Button
              onClick={requestClose}
              disabled={saving}
              sx={{ color: '#aaa', textTransform: 'none' }}
            >
              {t('climateSystem.schedules.discard')}
            </Button>
            <Button
              variant="contained"
              disabled={!dirty || saving}
              onClick={() => { void handleSave(); }}
              sx={{
                bgcolor: HEAT,
                color: '#111',
                fontWeight: 700,
                textTransform: 'none',
                borderRadius: '10px',
                boxShadow: `0 6px 18px ${HEAT}44`,
                '&:hover': { bgcolor: '#ff9e7a' },
                '&.Mui-disabled': { bgcolor: '#444', color: '#888' },
              }}
            >
              {saving
                ? t('common.saving', { defaultValue: 'Guardando…' })
                : t('climateSystem.schedules.save')}
            </Button>
          </Box>
        </>
      )}

      {error && (
        <Typography sx={{ color: '#e57373', fontSize: 12, mt: 1.5 }}>{error}</Typography>
      )}

      {/* Edit period */}
      <Dialog
        open={editIdx != null}
        onClose={() => { setEditingTime(false); setEditingTemp(false); setEditIdx(null); }}
        PaperProps={{ style: { backgroundColor: '#1E1E1E', color: 'white', minWidth: 'min(340px, calc(100vw - 32px))' } }}
      >
        <DialogTitle sx={{ color: ACCENT, fontSize: '1rem', fontWeight: 'bold' }}>
          {t('climateSystem.schedules.editPeriod')}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', mb: 0.5, letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 700 }}>
            {t('climateSystem.schedules.time')}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, mb: 2 }}>
            <RoundControl
              ariaLabel="-"
              onStep={() => { setEditingTime(false); setEditTimeMin((m) => m - 10); }}
            >
              <RemoveIcon />
            </RoundControl>
            {editingTime ? (
              <TextField
                autoFocus
                value={timeDraft}
                onChange={(e) => setTimeDraft(e.target.value)}
                onBlur={() => {
                  const parsed = parseTimeInput(timeDraft);
                  if (parsed == null) {
                    setError(t('climateSystem.schedules.invalidTime'));
                    setTimeDraft(formatTimeDisplay(minutesToTod(editTimeMin)));
                  } else {
                    setEditTimeMin(parsed);
                    setError(null);
                  }
                  setEditingTime(false);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                }}
                inputProps={{
                  inputMode: 'numeric',
                  style: {
                    textAlign: 'center',
                    fontSize: 28,
                    fontWeight: 700,
                    color: '#fff',
                    padding: '4px 0',
                  },
                }}
                sx={{
                  width: 110,
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'rgba(255,255,255,0.06)',
                    '& fieldset': { borderColor: ACCENT },
                  },
                }}
              />
            ) : (
              <Typography
                onClick={() => {
                  setTimeDraft(formatTimeDisplay(minutesToTod(editTimeMin)));
                  setEditingTime(true);
                }}
                sx={{
                  fontSize: 28,
                  fontWeight: 700,
                  fontVariantNumeric: 'tabular-nums',
                  minWidth: 90,
                  textAlign: 'center',
                  cursor: 'pointer',
                  borderBottom: '1px dashed rgba(255,255,255,0.25)',
                }}
              >
                {formatTimeDisplay(minutesToTod(editTimeMin))}
              </Typography>
            )}
            <RoundControl
              ariaLabel="+"
              onStep={() => { setEditingTime(false); setEditTimeMin((m) => m + 10); }}
            >
              <AddIcon />
            </RoundControl>
          </Box>
          <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', mb: 0.5, letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 700 }}>
            {t('climateSystem.schedules.temperature')}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
            <RoundControl
              ariaLabel="-"
              disabled={editTemp <= caps.minT}
              onStep={() => {
                setEditingTemp(false);
                setEditTemp((v) => Math.max(caps.minT, Math.round((v - caps.step) * 2) / 2));
              }}
            >
              <RemoveIcon />
            </RoundControl>
            {editingTemp ? (
              <TextField
                autoFocus
                value={tempDraft}
                onChange={(e) => setTempDraft(e.target.value)}
                onBlur={() => {
                  const parsed = parseTempInput(tempDraft, caps.minT, caps.maxT, caps.step);
                  if (parsed == null) {
                    setError(t('climateSystem.schedules.invalidTemp'));
                    setTempDraft(editTemp.toFixed(1));
                  } else {
                    setEditTemp(parsed);
                    setError(null);
                  }
                  setEditingTemp(false);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                }}
                inputProps={{
                  inputMode: 'decimal',
                  style: {
                    textAlign: 'center',
                    fontSize: 28,
                    fontWeight: 700,
                    color: evohomeTempColor(editTemp),
                    padding: '4px 0',
                  },
                }}
                sx={{
                  width: 110,
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'rgba(255,255,255,0.06)',
                    '& fieldset': { borderColor: ACCENT },
                  },
                }}
              />
            ) : (
              <Typography
                onClick={() => {
                  setTempDraft(editTemp.toFixed(1));
                  setEditingTemp(true);
                }}
                sx={{
                  fontSize: 28,
                  fontWeight: 700,
                  color: evohomeTempColor(editTemp),
                  minWidth: 90,
                  textAlign: 'center',
                  cursor: 'pointer',
                  borderBottom: '1px dashed rgba(255,255,255,0.25)',
                }}
              >
                {editTemp.toFixed(1)}°
              </Typography>
            )}
            <RoundControl
              ariaLabel="+"
              disabled={editTemp >= caps.maxT}
              onStep={() => {
                setEditingTemp(false);
                setEditTemp((v) => Math.min(caps.maxT, Math.round((v + caps.step) * 2) / 2));
              }}
            >
              <AddIcon />
            </RoundControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, justifyContent: 'space-between' }}>
          <Button
            startIcon={<DeleteOutlineIcon />}
            onClick={deletePeriod}
            sx={{ color: '#EF5350', textTransform: 'none' }}
          >
            {t('climateSystem.schedules.deletePeriod')}
          </Button>
          <Button onClick={commitEdit} sx={{ color: ACCENT, fontWeight: 700, textTransform: 'none' }}>
            {t('climateSystem.schedules.done')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Progreso copia zona → zonas */}
      <Dialog
        open={copyProgress != null}
        PaperProps={{ style: { backgroundColor: '#1E1E1E', color: 'white', minWidth: 'min(320px, calc(100vw - 32px))' } }}
      >
        <DialogTitle sx={{ color: ACCENT, fontSize: '1rem', fontWeight: 'bold' }}>
          {t('climateSystem.schedules.copyZone')}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, color: '#ccc', mb: 1.5 }}>
            {copyProgress
              ? t('climateSystem.schedules.copyProgressZone', {
                name: copyProgress.name,
                current: copyProgress.current,
                total: copyProgress.total,
              })
              : ''}
          </Typography>
          <LinearProgress
            variant={copyProgress && copyProgress.total > 0
              ? 'determinate'
              : 'indeterminate'}
            value={copyProgress && copyProgress.total > 0
              ? (100 * copyProgress.current) / copyProgress.total
              : 0}
            sx={{
              height: 8,
              borderRadius: 4,
              bgcolor: 'rgba(255,255,255,0.08)',
              '& .MuiLinearProgress-bar': { bgcolor: HEAT },
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Copy day */}
      <Dialog
        open={copyDayOpen}
        onClose={() => setCopyDayOpen(false)}
        PaperProps={{ style: { backgroundColor: '#1E1E1E', color: 'white', minWidth: 'min(360px, calc(100vw - 32px))' } }}
      >
        <DialogTitle sx={{ color: ACCENT, fontSize: '1rem', fontWeight: 'bold' }}>
          {t('climateSystem.schedules.copyDayTitle', { day: dayLabel(activeDay) })}
        </DialogTitle>
        <DialogContent>
          <FormGroup>
            <FormControlLabel
              control={(
                <Checkbox
                  checked={DAYS.filter((d) => d !== activeDay).every((d) => copyDayTargets[d])}
                  onChange={(_, checked) => {
                    const next: Record<string, boolean> = {};
                    DAYS.forEach((d) => { if (d !== activeDay) next[d] = checked; });
                    setCopyDayTargets(next);
                  }}
                  sx={{ color: HEAT, '&.Mui-checked': { color: HEAT } }}
                />
              )}
              label={t('climateSystem.schedules.copyAllDays')}
            />
            {DAYS.filter((d) => d !== activeDay).map((d) => (
              <FormControlLabel
                key={d}
                control={(
                  <Checkbox
                    checked={Boolean(copyDayTargets[d])}
                    onChange={(_, checked) => setCopyDayTargets((p) => ({ ...p, [d]: checked }))}
                    sx={{ color: HEAT, '&.Mui-checked': { color: HEAT } }}
                  />
                )}
                label={dayLabel(d)}
              />
            ))}
          </FormGroup>
          <Typography sx={{ fontSize: 12, color: '#999', mt: 1 }}>
            {t('climateSystem.schedules.copyDayHint')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCopyDayOpen(false)} sx={{ color: '#aaa', textTransform: 'none' }}>
            {t('common.cancel')}
          </Button>
          <Button onClick={applyCopyDay} sx={{ color: ACCENT, fontWeight: 700, textTransform: 'none' }}>
            {t('climateSystem.schedules.copyApply')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Copy zone */}
      <Dialog
        open={copyZoneOpen}
        onClose={() => setCopyZoneOpen(false)}
        PaperProps={{ style: { backgroundColor: '#1E1E1E', color: 'white', minWidth: 'min(360px, calc(100vw - 32px))' } }}
      >
        <DialogTitle sx={{ color: ACCENT, fontSize: '1rem', fontWeight: 'bold' }}>
          {t('climateSystem.schedules.copyZoneTitle', { name: zone.nombre })}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 12, color: '#ccc', mb: 1 }}>
            {t('climateSystem.schedules.copyZoneWarn')}
          </Typography>
          <FormGroup>
            <FormControlLabel
              control={(
                <Checkbox
                  checked={otherZones.length > 0 && otherZones.every((z) => copyZoneTargets[z.id])}
                  onChange={(_, checked) => {
                    const next: Record<number, boolean> = {};
                    otherZones.forEach((z) => { next[z.id] = checked; });
                    setCopyZoneTargets(next);
                  }}
                  sx={{ color: HEAT, '&.Mui-checked': { color: HEAT } }}
                />
              )}
              label={t('climateSystem.schedules.copyAllZones')}
            />
            {otherZones.map((z) => (
              <FormControlLabel
                key={z.id}
                control={(
                  <Checkbox
                    checked={Boolean(copyZoneTargets[z.id])}
                    onChange={(_, checked) => setCopyZoneTargets((p) => ({ ...p, [z.id]: checked }))}
                    sx={{ color: HEAT, '&.Mui-checked': { color: HEAT } }}
                  />
                )}
                label={z.nombre}
              />
            ))}
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCopyZoneOpen(false)} sx={{ color: '#aaa', textTransform: 'none' }}>
            {t('common.cancel')}
          </Button>
          <Button
            disabled={!otherZones.some((z) => copyZoneTargets[z.id]) || saving}
            onClick={() => { void handleCopyZone(); }}
            sx={{ color: HEAT, fontWeight: 700, textTransform: 'none' }}
          >
            {t('climateSystem.schedules.copyAndSave')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={discardOpen}
        onClose={() => setDiscardOpen(false)}
        PaperProps={{ style: { backgroundColor: '#1E1E1E', color: 'white' } }}
      >
        <DialogTitle sx={{ color: ACCENT, fontWeight: 'bold', fontSize: '1rem' }}>
          {t('climateSystem.schedules.unsavedTitle')}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#ccc', fontSize: 13 }}>
            {t('climateSystem.schedules.unsavedBody')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDiscardOpen(false)} sx={{ color: '#aaa', textTransform: 'none' }}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={() => { setDiscardOpen(false); onClose(); }}
            sx={{ color: '#EF5350', fontWeight: 700, textTransform: 'none' }}
          >
            {t('climateSystem.schedules.discard')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(snack)}
        autoHideDuration={3500}
        onClose={() => setSnack(null)}
        {...getDemoRestrictionSnackbarProps()}
      >
        <Alert onClose={() => setSnack(null)} severity="success" sx={{ width: '100%' }}>
          {snack}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EvohomeScheduleEditor;
